/* eslint-disable unicorn/prefer-string-slice */
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  ElementNode,
  LexicalNode,
} from "lexical";
import { $isGenericMentionNode } from "./social-mention/mention-decorator-node";

interface NormalTextSpan {
  type: "normal";
  text: string;
}

interface RegexSpan {
  type: "regex";
  match: RegExpExecArray;
}

export type ParsedTextSpan = RegexSpan | NormalTextSpan;

function exportTopLevel(node: LexicalNode): string | undefined {
  if ($isGenericMentionNode(node)) {
    return node.platformText;
  }
  if ($isElementNode(node)) {
    const children = node.getChildren();
    return children.map((child) => exportTopLevel(child)).join("");
  }
  return node.getTextContent();
}

export function buildSpansWithMatchedRegex(
  text: string,
  regex: RegExp
): ParsedTextSpan[] {
  const matches = [...text.matchAll(regex)];
  if (matches.length <= 0) {
    return [{ type: "normal", text }];
  }
  const spans: ParsedTextSpan[] = [];
  for (let idx = 0; idx < matches.length; idx++) {
    const match = matches[idx];
    const prevMatch = idx > 0 ? matches[idx - 1] : undefined;
    spans.push(
      {
        type: "normal",
        text: text.substring(
          prevMatch ? prevMatch.index + prevMatch[0].length : 0,
          match.index
        ),
      },
      { type: "regex", match }
    );
    if (idx >= matches.length - 1) {
      spans.push({
        type: "normal",
        text: text.substring(match.index + match[0].length, text.length),
      });
    }
  }

  return spans.filter((c) => c.type !== "normal" || c.text.length > 0);
}

export const regexConversionFactory = (params?: {
  regex: RegExp;
  func: (match: RegExpExecArray) => LexicalNode | null | undefined;
}) => {
  const { regex, func } = params || {};
  function parseLine(span: NormalTextSpan): ParsedTextSpan[] {
    if (!regex) {
      return [span];
    }

    return buildSpansWithMatchedRegex(span.text, regex);
  }

  return (caption: string) => {
    const elementNode = $createParagraphNode();
    // eslint-disable-next-line unicorn/no-array-for-each
    caption.split(/\r?\n/).forEach((line, index, array) => {
      const spans = parseLine({ text: line, type: "normal" });
      for (const span of spans) {
        if (span.type === "normal") {
          const textNode = $createTextNode(span.text);
          elementNode.append(textNode);
        } else if (span.type === "regex") {
          const customNode = func?.(span.match);
          if (customNode) {
            elementNode.append(customNode);
          } else {
            const textNode = $createTextNode(span.match[0]);
            elementNode.append(textNode);
          }
        }
      }
      if (index < array.length - 1) {
        const br = $createLineBreakNode();
        elementNode.append(br);
      }
    });
    return elementNode;
  };
};

export const commonTextExport = (node?: ElementNode) => {
  const children = (node || $getRoot()).getChildren();
  return children
    .map((child) => {
      return exportTopLevel(child);
    })
    .join("");
};

export const commonTextImport = regexConversionFactory();
